/**
 * Date utilities — все операции в UTC+5 (Оренбург, бизнес-таймзона).
 * Copied 1:1 from v3 utils/date.ts. Separate from DateSelector/dateUtils.ts
 * which deals only with local-time calendar rendering.
 */

import { isNightSlot } from "@ub/shared";

const UTC5_OFFSET = 5 * 3_600_000;

/** Текущее время в UTC+5. */
export function nowUtc5(): Date {
  return new Date(Date.now() + UTC5_OFFSET);
}

/** Любую дату → UTC+5 Date. */
export function toUtc5(d: Date): Date {
  return new Date(d.getTime() + UTC5_OFFSET);
}

/** Краткие русские месяцы (nominative-style — для compact DC grid header). */
const MONTHS = [
  "янв", "фев", "мар", "апр", "май", "июн",
  "июл", "авг", "сен", "окт", "ноя", "дек",
] as const;

/** Краткие дни недели (Воскресенье=0). */
const DAYS = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"] as const;

/** Форматирование: "28 мар · сб". */
export function fmtDate(d: Date): string {
  const day = d.getUTCDate();
  const month = MONTHS[d.getUTCMonth()] ?? "";
  const dow = DAYS[d.getUTCDay()] ?? "";
  return `${day} ${month} · ${dow}`;
}

/** Форматирование для API: "2026-03-28". */
export function fmtDateApi(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Quick-pick label + date. */
export interface QuickDate {
  label: string;
  date: Date;
}

/** Сегодня, завтра, ближайшие сб/вс в UTC+5 (для preset-чипов рядом с DateSelector). */
export function getWeekendDates(base: Date): QuickDate[] {
  const dates: QuickDate[] = [];
  const added = new Set<string>();

  dates.push({ label: "сегодня", date: new Date(base) });
  added.add(fmtDateApi(base));

  const tomorrow = addDays(base, 1);
  dates.push({ label: "завтра", date: tomorrow });
  added.add(fmtDateApi(tomorrow));

  for (let offset = 2; offset < 28 && dates.length < 8; offset++) {
    const d = addDays(base, offset);
    const dayOfWeek = d.getUTCDay();
    if (dayOfWeek === 6 || dayOfWeek === 0) {
      const key = fmtDateApi(d);
      if (!added.has(key)) {
        const dayName = DAYS[dayOfWeek] ?? "";
        const dayNum = d.getUTCDate();
        const monthName = MONTHS[d.getUTCMonth()] ?? "";
        dates.push({ label: `${dayName} ${dayNum} ${monthName}`, date: d });
        added.add(key);
      }
    }
  }
  return dates;
}

/** Ночной слот (< 06:00 — следующий день): канон переехал в @ub/shared (A48). */
export { isNightSlot };

/** Сортировка времён слотов: дневные сначала, ночные в конец. */
export function slotSort(a: string, b: string): number {
  const aN = isNightSlot(a);
  const bN = isNightSlot(b);
  if (aN === bN) return a.localeCompare(b);
  return aN ? 1 : -1;
}

/** Добавить N дней к UTC дате. */
export function addDays(d: Date, n: number): Date {
  const next = new Date(d);
  next.setUTCDate(next.getUTCDate() + n);
  return next;
}

/** "2026-03-28" → Date в UTC. */
export function parseDateApi(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(Date.UTC(y ?? 2026, (m ?? 1) - 1, d ?? 1));
}

/**
 * Today as a business-day Date — UTC-midnight of the current Orenburg date.
 *
 * Background: the wire format is "YYYY-MM-DD" and `fmtDateApi` reads UTC
 * methods. If you take `new Date()` in any timezone east of UTC and
 * normalise to local midnight, the UTC date silently rolls back by one day
 * (UTC+5 local 00:00 = UTC 19:00 of the previous day). So always build
 * grid dates here, not with `new Date(local.year, local.month, local.date)`.
 */
export function todayBusiness(): Date {
  const n = nowUtc5();
  return new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()));
}

/**
 * Convert a Date built in the user's local timezone (e.g. what M.1
 * DateSelector emits — its calendar grid uses local methods) into a
 * UTC-midnight Date with the same calendar fields, so `fmtDateApi` reads
 * the business day the user intended.
 */
export function toBusinessDate(local: Date): Date {
  return new Date(Date.UTC(local.getFullYear(), local.getMonth(), local.getDate()));
}

/**
 * Inverse — convert a UTC-midnight business Date back to a local-zone Date
 * with the same Y/M/D, suitable for feeding to M.1 DateSelector's
 * `initialDate` prop.
 */
export function fromBusinessDate(business: Date): Date {
  return new Date(business.getUTCFullYear(), business.getUTCMonth(), business.getUTCDate());
}

/** Ночной хвост смены (27.08, владелец): игры до 06:00 календарного дня D
 *  принадлежат смене дня D-1 — «смена уже стоит на филиале». День D в
 *  расстановке и дашбордах показывает свои игры с 06:00 плюс ночные игры
 *  дня D+1 (до 06:00). */
export const NIGHT_CUTOFF = "06:00";
export function isNightTail(time: string | null | undefined): boolean {
  const t = (time || "").slice(0, 5);
  return /^\d{2}:\d{2}$/.test(t) && t < NIGHT_CUTOFF;
}
